import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BmModal, Btn, EmptyState, ErrorState, NoticeState, PageHeader } from "../../ForBrands/ui/BrandUI";
import { MobileEmpty, MobilePageHead } from "../../ForBrands/ui/mobile/MobileUI";
import { PhoneThemeProvider, usePhoneTheme } from "../../ForBrands/useIsPhone";
import RosterManagerModal from "../../ForBusiness/CoachDashboard/RosterManagerModal";
import { GENDERS, SPORTS, legacySportKey } from "../../ForBusiness/Cacheshare/taxonomy";
import { cerebroScopeFor } from "../../ForBusiness/Cacheshare/services/cacheShareServices";
import { dataStatusNotice, filterOptions, filterRows, fmtMoney2, lockedTeamOf, paginate, programSportGender, teamKeyOf } from "../../ForBusiness/Cacheshare/allocationModel";
import { useAllocationBoard } from "../../ForBusiness/Cacheshare/hooks/useAllocationBoard";
import { useCoachCacheShareSession } from "../../ForBusiness/Cacheshare/hooks/useCoachCacheShareSession";
import { useCoachTeams } from "../../ForBusiness/Cacheshare/hooks/useCoachTeams";
import { useTeamCascade } from "../../ForBusiness/Cacheshare/hooks/useTeamCascade";
import { spacedSlash, teamDisplayName } from "../../ForBusiness/Cacheshare/hooks/useCascadeFields";
import AllocationTables from "../../ForBusiness/Cacheshare/v2/AllocationTables";
import BudgetCard from "../../ForBusiness/Cacheshare/v2/BudgetCard";
import CacheShareRail from "../../ForBusiness/Cacheshare/v2/CacheShareRail";
import KpiStrip from "../../ForBusiness/Cacheshare/v2/KpiStrip";
import RosterToolbar from "../../ForBusiness/Cacheshare/v2/RosterToolbar";
import TeamCard from "../../ForBusiness/Cacheshare/v2/TeamCard";
import { teamRowKey } from "../../ForBusiness/Cacheshare/v2/TeamList";
import { Pager } from "../../ForBusiness/Cacheshare/v2/allocationUi";
import { FLOOR_NAMES, revshareFloorName } from "../../ForBusiness/Cacheshare/v2/allocationCells";
import AllocationAddPlayerModal from "./AllocationAddPlayerModal";
import { AllocationAccessGate, AllocationPendingGate } from "./allocationPieces";
import { useCoachModeOptional } from "./CoachModeContext";
import { teamNameOf } from "./coachUtils";

// Coach Mode · CacheShare™ (Figma 03b coach view, 03 admin view, 03a Add
// player). Coaches model their locked team, with the budget, imports,
// hidden players and locked amounts saved per team; admins pick any team
// from the selection rail, and nothing is saved. In the phone theme (below
// 640px) the pieces switch to their phone forms (Figma 2416:2527): a
// compact head, 18px between sections, player cards and 25 players a page.
// Admins get the same phone forms from the viewport alone, in Admin Mode
// too (D-cs4, 2418:2178).

const COACH_PERMS = ["IS_COACH", "IS_RECRUITER_SCOUT", "IS_BRAND_AGENCY", "IS_AD_PD"];
const PLANS = ["coach_core", "coach_pro", "coach_premium", "admin"];
const NO_FILTERS = { position: "", number: "", classification: "" };
// Players a page until the coach picks a size: 100 rows in the tables, 25
// cards (about 320px each) on phones (D-cs3).
const PAGE_SIZE = 100;
const PHONE_PAGE_SIZE = 25;
const NO_TEAM = "Your coach profile has no team yet. Add your team on your profile to model its CacheShare™.";

// Page head: the V2 PageHeader, or on phones the compact head (no eyebrow
// unless `phoneEyebrow`, Body/S subtitle).
const CacheShareHead = ({ eyebrow, phoneEyebrow, title, subtitle }) =>
  usePhoneTheme() ? <MobilePageHead eyebrow={phoneEyebrow} title={title} sub={subtitle} /> : <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} />;

// What did not finish, ahead of the board's failure text.
const FAILED = {
  load: "This team's roster did not load.",
  distribute: "Distribute did not finish.",
};
const NOT_LOADED = "The roster did not load. Use Try again above.";
const POOL_NAMES = { rs: "Revenue share", nil: "NIL" };

// A critical banner with Try again.
const RetryBanner = ({ text, onRetry, disabled = false }) => {
  const phone = usePhoneTheme();
  return (
    <ErrorState role="alert" className={phone ? "flex flex-col gap-3" : "flex flex-wrap items-center justify-between gap-3"}>
      <span className="min-w-0 break-words">{text}</span>
      <Btn variant="secondary" size={phone ? "lg" : "sm"} className={phone ? "w-full" : "shrink-0"} onClick={onRetry} disabled={disabled}>
        Try again
      </Btn>
    </ErrorState>
  );
};

// A calculator call that failed (after the board's own second try): what
// went wrong, the server's reason when it refused, and Try again.
const BoardError = ({ board }) => {
  if (!board.error) return null;
  const text = `${FAILED[board.error.action] || FAILED.load} ${board.error.text}`;
  return <RetryBanner text={text} onRetry={board.retry} disabled={board.loading || board.distributing} />;
};

// The roster came from the provider's saved copy (the feed is paused), or
// added players have no saved stats: say so, with the season and the date
// the stats were saved.
const DataNotice = ({ status }) => {
  const text = dataStatusNotice(status);
  return text ? <NoticeState role="status">{text}</NoticeState> : null;
};

// The split did not come back (the allocate endpoint): the tables keep the
// last one, with Try again.
const AllocateError = ({ board }) => {
  if (board.allocation.status !== "error") return null;
  return <RetryBanner text="We could not update the split. Try again." onRetry={board.retryAllocate} />;
};

// What put a pool over budget and what fixes it: the locks, or the players'
// own minimums, named by the rows they raised. In revenue share that is a
// player's value floor (the low end of his PerformanceValue, named neutrally,
// notes 10/21 D3), or the CacheValue of a player valued before
// PerformanceValue (allocationCells.revshareFloorName, 10/19 D13); in NIL
// his NILx value.
const OVER = {
  rs: { hidden: "hiddenRs", locked: "rsLocked", floored: "rsFloored" },
  nil: { hidden: "hiddenNil", locked: "nilLocked", floored: "nilFloored" },
};
const minimumOf = (pool, row) => (pool === "nil" ? "NILx values" : `${revshareFloorName(row)}s`);
const MINIMUM_ORDER = [`${FLOOR_NAMES.performanceValue}s`, `${FLOOR_NAMES.cacheValue}s`, "NILx values"];
// "A", "A and B", "A, B and C".
const wordList = (words) => (words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}` : words[0] || "");
export const overFix = (pool, rows) => {
  const { hidden, locked, floored } = OVER[pool];
  const visible = rows.filter((r) => !r[hidden]);
  const locks = visible.some((r) => r[locked]);
  const raised = visible.filter((r) => r[floored]);
  const values = MINIMUM_ORDER.filter((name) => raised.some((r) => minimumOf(pool, r) === name));
  if (values.length && locks) return `${wordList(["Locked amounts", ...values])} add up to more than the budget. Lower an amount or raise the budget.`;
  if (values.length) return `Players' ${wordList(values)} add up to more than the budget. Raise the budget or type a lower amount for a player.`;
  return "Lower a locked amount or raise the budget.";
};

// One banner for each pool over budget, by how much and what fixes it.
const OverBudget = ({ pools, rows }) =>
  Object.entries(POOL_NAMES)
    .filter(([pool]) => pools[pool].over)
    .map(([pool, name]) => (
      <ErrorState key={pool} role="alert">
        {`${name} is over budget by ${fmtMoney2(-pools[pool].remaining)}. ${overFix(pool, rows)}`}
      </ErrorState>
    ));

const openProfile = (row) => {
  localStorage.setItem("athleteEmail", row.email);
  window.open(`/business/athlette/profile/${row.athleteId || ""}`, "_blank");
};

// KPIs, budget, roster head, the two tables and the pager, shared by the
// coach and admin views. The tables sit side by side when the full revenue
// share table fits beside the NIL table, else stacked (AllocationTables).
const AllocationWorkspace = ({ board, destination, budgetSub, importDefaults, programLabel, manage, emptyText, booting = false }) => {
  const phone = usePhoneTheme();
  const [pool, setPool] = useState("rs");
  const [filters, setFilters] = useState(NO_FILTERS);
  const [page, setPage] = useState(1);
  const [pickedSize, setPickedSize] = useState(null);
  const size = pickedSize ?? (phone ? PHONE_PAGE_SIZE : PAGE_SIZE);
  const [addOpen, setAddOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    setFilters(NO_FILTERS);
    setPage(1);
  }, [board.openedKey]);

  const options = useMemo(() => filterOptions(board.rows), [board.rows]);
  const view = paginate(filterRows(board.rows, filters), page, size);
  const ready = !!board.openedKey && !board.loading;

  return (
    <>
      <KpiStrip pools={board.pools} pool={pool} onPool={setPool} budgetSub={budgetSub} />
      <BudgetCard
        amounts={board.amounts}
        onAmount={board.setAmount}
        onSettle={board.settleAmount}
        onDistribute={board.distribute}
        canDistribute={board.canDistribute}
        distributing={board.distributing}
      />
      <OverBudget pools={board.pools} rows={board.rows} />
      <BoardError board={board} />
      <DataNotice status={board.dataStatus} />
      <AllocateError board={board} />
      <RosterToolbar
        count={board.rows.length}
        options={options}
        filters={filters}
        onFilter={(key, value) => {
          setFilters((f) => ({ ...f, [key]: value }));
          setPage(1);
        }}
        canManage={!!manage}
        onManage={() => setManageOpen(true)}
        canAdd={ready}
        onAdd={() => setAddOpen(true)}
      />
      <AllocationTables
        rows={view.rows}
        loading={booting || board.loading}
        emptyText={board.error ? NOT_LOADED : emptyText}
        onToggle={board.toggleHidden}
        onLock={board.setLock}
        onOpenProfile={openProfile}
        onRemove={board.removePlayer}
      />
      {view.total > 0 && (
        <Pager
          {...view}
          size={size}
          onPage={setPage}
          onSize={(n) => {
            setPickedSize(n);
            setPage(1);
          }}
        />
      )}
      <AllocationAddPlayerModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onImport={board.importPlayers}
        destination={destination}
        defaults={importDefaults}
        existingIds={board.existingIds}
        programLabel={programLabel}
      />
      {manage && (
        <RosterManagerModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          teamId={manage.teamId}
          league={manage.league}
          teamName={manage.teamName}
          roster={board.rows.filter((r) => !r.added)}
          onChanged={board.reload}
        />
      )}
    </>
  );
};

const defaultsFor = (sport, gender) => ({ sport: SPORTS.find((s) => s.value === sport) || null, gender: GENDERS.find((g) => g.value === gender) || null });

// Coaches. Inside the Coach Mode shell the team picked here is the team
// Home and Team show, and the other way round; outside it (no shell state)
// the page keeps its own pick, starting from the primary team.
const CoachCacheShare = ({ cv }) => {
  const coachMode = useCoachModeOptional();
  const fallback = cv?.teamId ? { teamId: cv.teamId, league: cv.league || "", currentTeam: cv.currentTeam || "", cascade: cv.cascade || {}, isPrimary: true } : null;
  const teams = useCoachTeams(fallback, coachMode?.selection.team || null);
  const record = teams.active;
  const key = teams.activeKey;
  const program = programSportGender(record?.cascade || {}, record?.league || "");
  const sport = legacySportKey(program.sport, program.gender) || "";
  const team = useMemo(() => lockedTeamOf(record), [record]);
  const board = useAllocationBoard({ team, sport, scope: cerebroScopeFor(team, null, sport) });
  const session = useCoachCacheShareSession({ teamKey: board.openedKey, slice: board.slice });

  useEffect(() => {
    if (!teams.loaded || session.status === "loading") return;
    board.open(record ? key : "", record ? session.sliceFor(record) : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, teams.loaded, session.status]);

  const switchTeam = (teamKey) => {
    teams.setActiveKey(teamKey);
    const picked = teams.teams.find((t) => teamKeyOf(t) === teamKey);
    if (picked) coachMode?.selectTeam(picked);
  };
  const leagueAdmin = teams.leagueAdmin && {
    ...teams.leagueAdmin,
    onViewMode: (mode) => {
      teams.leagueAdmin.onViewMode(mode);
      coachMode?.switchView(mode);
    },
  };

  const phone = usePhoneTheme();
  const defaults = defaultsFor(program.sport, program.gender);
  // The team by the one display rule ("Oakland Soldiers 16U", D15): the
  // subtitle, the "Adding to" line, the budget subline and the Manage roster
  // title. Identity stays on teamId and league.
  const name = teamNameOf(record);
  const noTeam = teams.loaded && !record;
  const head = (
    <CacheShareHead
      eyebrow="Coach Mode"
      title="CacheShare™"
      subtitle={`Model how a NIL pool splits across ${name || "your roster"}. Your budget and imported players save to your coach account.`}
    />
  );
  const body =
    noTeam && !leagueAdmin ? (
      phone ? (
        <MobileEmpty>{NO_TEAM}</MobileEmpty>
      ) : (
        <EmptyState>{NO_TEAM}</EmptyState>
      )
    ) : (
      <>
        {(record || leagueAdmin) && <TeamCard record={record} teams={teams.teams} activeKey={key} onSwitch={switchTeam} leagueAdmin={leagueAdmin} />}
        {!noTeam && (
          <AllocationWorkspace
            board={board}
            destination={name}
            budgetSub={[name, defaults.gender?.name].filter(Boolean).join(" · ")}
            importDefaults={defaults}
            programLabel="your program"
            manage={record ? { teamId: record.teamId, league: record.league || "", teamName: name } : null}
            emptyText="No players came back for this team. Add players, or manage the roster."
            booting={!teams.loaded || session.status === "loading"}
          />
        )}
      </>
    );
  // Phones: the head and the sections 18px apart (the shell already pads
  // the page). One tree shape at every width, so the workspace keeps its
  // filters and page when the window crosses 640px.
  return (
    <div className={phone ? "flex flex-col gap-[18px]" : undefined}>
      {head}
      <div className={phone ? "flex flex-col gap-[18px]" : "flex flex-col gap-6"}>{body}</div>
    </div>
  );
};

// Admin header: the selected team, else the tool itself.
const adminHeader = (team, filters) => {
  if (!team) return { eyebrow: "Admin", title: "CacheShare™", subtitle: "Pick a sport, tier and team to model a NIL pool." };
  const place = team.market || team.city || "";
  return {
    eyebrow: teamDisplayName(team),
    title: `${place || team.name || ""} ${filters.sport?.name || ""}`.trim(),
    subtitle: [spacedSlash(filters.gender?.name), filters.subDivision?.name, filters.body?.name].filter(Boolean).join(" · "),
  };
};

// Admins. From 640px up the selection is a bar above the workspace
// (CacheShareRail variant="bar") rather than a 320px rail beside it, so the
// workspace has the page's full width and the revenue share and NIL tables
// sit side by side as in Coach Mode; picking a team folds the bar to a
// summary with Change selection. On phones (2418:2178) the selection is the
// "Build your selection" button under the head, which opens the rail full
// screen (Selection filters 2420:2373); picking a team or the X closes it
// (D-cs6). No "Your team" card, and the pager stays. The workspace keeps
// its place in the tree at every width, so its filters and page survive a
// rotation. `topGap` adds the 20px the Coach Mode shell puts above its
// phone pages (Admin Mode has no phone chrome).
const AdminCacheShare = ({ topGap = false }) => {
  const phone = usePhoneTheme();
  const cascade = useTeamCascade();
  const { filters, plan, selectedTeam } = cascade;
  const sport = legacySportKey(filters.sport?.value, filters.gender?.value) || "";
  const board = useAllocationBoard({ team: selectedTeam, sport, scope: cerebroScopeFor(selectedTeam, plan, sport) });
  const [railOpen, setRailOpen] = useState(false);
  const [barOpen, setBarOpen] = useState(true);
  const closeRail = () => setRailOpen(false);

  // A new team keeps the budgets typed so far.
  useEffect(() => {
    board.open(selectedTeam ? teamRowKey(selectedTeam) : "", { revenueAmount: board.amounts.rs, nilAmount: board.amounts.nil });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam]);

  // Wider than a phone the selection is the bar again.
  useEffect(() => {
    if (!phone) setRailOpen(false);
  }, [phone]);

  // A picked team folds the bar to its summary (the tables move up); no
  // team opens it again.
  useEffect(() => {
    setBarOpen(!selectedTeam);
  }, [selectedTeam]);

  const header = adminHeader(selectedTeam, filters);
  const name = teamDisplayName(selectedTeam);
  // Phones have no bar on screen, so the empty roster points at the button.
  let emptyText = "Pick a team in Build your selection to load its roster.";
  if (selectedTeam) emptyText = "No players came back for this team. Try another team or add players.";
  else if (phone) emptyText = "Open Build your selection and pick a team to load its roster.";
  let rootClass;
  if (phone) rootClass = topGap ? "pt-5" : undefined;
  return (
    <div className={rootClass}>
      <div className={phone ? "flex flex-col gap-[18px]" : "flex min-w-0 flex-col"}>
        {phone ? (
          <MobilePageHead eyebrow={header.eyebrow} title={header.title} sub={header.subtitle}>
            <Btn size="lg" variant="secondary" className="w-full" aria-haspopup="dialog" onClick={() => setRailOpen(true)}>
              Build your selection
            </Btn>
          </MobilePageHead>
        ) : (
          <PageHeader eyebrow={header.eyebrow} title={header.title} subtitle={header.subtitle} />
        )}
        <div className={phone ? "flex flex-col gap-[18px]" : "flex flex-col gap-6"}>
          {phone ? null : (
            <CacheShareRail variant="bar" cascade={cascade} open={barOpen || !selectedTeam} onToggle={selectedTeam ? () => setBarOpen((o) => !o) : undefined} />
          )}
          <AllocationWorkspace
            board={board}
            destination={name}
            budgetSub={[name, filters.gender?.name].filter(Boolean).join(" · ")}
            importDefaults={{ sport: filters.sport, gender: filters.gender }}
            programLabel="this roster"
            manage={
              selectedTeam
                ? { teamId: selectedTeam.id, league: selectedTeam.leagueKey || selectedTeam.cerebroLeague || selectedTeam.cacheLeague || selectedTeam.srSport || "", teamName: name }
                : null
            }
            emptyText={emptyText}
          />
        </div>
      </div>
      {phone && railOpen && (
        <BmModal presentation="fullscreen" title="CacheShare" onClose={closeRail}>
          <CacheShareRail variant="sheet" cascade={cascade} onTeamPick={closeRail} />
        </BmModal>
      )}
    </div>
  );
};

// The gates, under the page head.
const GatePage = ({ gate, isAdmin, topGap = false }) => {
  const phone = usePhoneTheme();
  let rootClass;
  if (phone) rootClass = topGap ? "flex flex-col gap-[18px] pt-5" : "flex flex-col gap-[18px]";
  return (
    <div className={rootClass}>
      <CacheShareHead eyebrow={isAdmin ? "Admin" : "Coach Mode"} phoneEyebrow={isAdmin ? "Admin" : undefined} title="CacheShare™" subtitle="Model how a NIL pool splits across your roster." />
      <div className={phone ? undefined : "pt-2"}>{gate}</div>
    </div>
  );
};

const CoachAllocationPage = () => {
  const user = useSelector((state) => state.user.user);
  const entitlement = useSelector((state) => state.entitlement?.entitlement);
  const perms = user?.permissions || [];
  const isAdmin = perms.includes("IS_ADMIN") || perms.includes("IS_CACHE_AI");
  const isCoach = !isAdmin && COACH_PERMS.some((p) => perms.includes(p));
  const cv = user?.coachVerification;

  // True on a phone inside the Coach Mode shell, which draws the phone
  // chrome and pads the page; false in Admin Mode (/cacheshare-tool).
  const shellPhone = usePhoneTheme();

  const gate = isCoach && cv?.status !== "verified" ? <AllocationPendingGate status={cv?.status} /> : !PLANS.includes(entitlement) || !(isAdmin || isCoach) ? <AllocationAccessGate /> : null;
  if (!isAdmin) return gate ? <GatePage gate={gate} isAdmin={false} /> : <CoachCacheShare cv={cv} />;
  // Admins take the phone layout from the viewport in any shell (D-cs4):
  // inside this provider usePhoneTheme() is the viewport check alone.
  return <PhoneThemeProvider>{gate ? <GatePage gate={gate} isAdmin topGap={!shellPhone} /> : <AdminCacheShare topGap={!shellPhone} />}</PhoneThemeProvider>;
};

export default CoachAllocationPage;
